// Hub-level interlinking: featured country deep-link grid.
// Renders featured destinations linking to /$service/$country pages.
import { Link } from "@tanstack/react-router";
import { APOSTILLE_COUNTRIES } from "@/data/apostille-countries";
import { MARRIAGE_ABROAD } from "@/data/marriage-abroad";
import { TARGET_COUNTRIES } from "@/data/police-target-countries";

type Service = "apostille" | "embassy" | "mfa-legalisation" | "marriage-abroad" | "police-clearance";
type Props = { service: Service; lang: "th" | "en" };

const FEATURED = ["germany", "uk", "usa", "australia", "france", "japan", "china", "uae", "canada", "singapore", "netherlands", "south-korea"];

const COPY = {
  th: {
    apostille: { h: "คู่มือ Apostille รายประเทศ", sub: "อ่านขั้นตอน + ค่าธรรมเนียม + หน่วยงานปลายทางแยกตามประเทศ" },
    embassy: { h: "รับรองสถานทูตในไทย รายประเทศ", sub: "ค่าธรรมเนียม เอกสารยอดนิยม และภาษาที่รับ" },
    "mfa-legalisation": { h: "MFA Legalisation รายประเทศ", sub: "เอกสารที่ต้องรับรองก่อนส่งไปแต่ละประเทศ" },
    "marriage-abroad": { h: "จดทะเบียนสมรสในต่างประเทศ รายประเทศ", sub: "เอกสาร + waiting period + สิทธิ same-sex แยกตามประเทศ" },
    "police-clearance": { h: "ตรวจประวัติอาชญากรรม รายประเทศปลายทาง", sub: "วีซ่า + หน่วยงานปลายทาง + Apostille/Legalization" },
  },
  en: {
    apostille: { h: "Apostille guides by country", sub: "Fees, accepting authority and step-by-step per destination" },
    embassy: { h: "Embassy legalization by country", sub: "Government fees, top documents, accepted languages" },
    "mfa-legalisation": { h: "MFA Legalisation by country", sub: "What to legalize before shipping to each destination" },
    "marriage-abroad": { h: "Marriage abroad by country", sub: "Documents, waiting period, same-sex recognition per destination" },
    "police-clearance": { h: "Police Clearance by destination country", sub: "Visa types, accepting authority, Apostille or Legalization" },
  },
} as const;

type Item = { slug: string; flag: string; nameTh: string; nameEn: string; badge: string; sub: string };

function pickItems(service: Service, lang: "th" | "en"): Item[] {
  if (service === "marriage-abroad") {
    return MARRIAGE_ABROAD.slice(0, 8).map((x) => ({
      slug: x.slug, flag: "💍", nameTh: x.th, nameEn: x.en,
      badge: x.hague ? "Apostille" : "Legalization",
      sub: `${x.waitingPeriodDays}d waiting · ${x.translationLang}`,
    }));
  }
  if (service === "police-clearance") {
    return TARGET_COUNTRIES.slice(0, 8).map((x) => ({
      slug: x.slug, flag: "🛂", nameTh: x.nameTh, nameEn: x.nameEn,
      badge: x.hague ? "Apostille" : "Legalization",
      sub: x.visaTypes[0] ?? "Visa/Immigration",
    }));
  }
  const items = FEATURED.map((s) => APOSTILLE_COUNTRIES.find((x) => x.slug === s)).filter(Boolean).slice(0, 8) as any[];
  return items.map((x) => ({
    slug: x.slug, flag: x.flag, nameTh: x.nameTh, nameEn: x.nameEn,
    badge: x.isHague ? "Apostille" : "Legalization",
    sub: x.turnaround,
  }));
}

function toTarget(service: Service, lang: "th" | "en", slug: string) {
  const enEmbassyFallback = lang === "en" && service === "embassy";
  if (enEmbassyFallback) return { to: "/en/apostille/$country" as const, params: { country: slug } };
  switch (service) {
    case "apostille":
      return lang === "en"
        ? { to: "/en/apostille/$country" as const, params: { country: slug } }
        : { to: "/apostille/$country" as const, params: { country: slug } };
    case "embassy":
      return { to: "/embassy/$country" as const, params: { country: slug } };
    case "mfa-legalisation":
      return lang === "en"
        ? { to: "/en/mfa-legalisation/$country" as const, params: { country: slug } }
        : { to: "/mfa-legalisation/$country" as const, params: { country: slug } };
    case "marriage-abroad":
      return lang === "en"
        ? { to: "/en/marriage/abroad/$country" as const, params: { country: slug } }
        : { to: "/marriage/abroad/$country" as const, params: { country: slug } };
    case "police-clearance":
      return lang === "en"
        ? { to: "/en/police-clearance/for/$country" as const, params: { country: slug } }
        : { to: "/police-clearance/for/$country" as const, params: { country: slug } };
  }
}

export function HubCountryLinks({ service, lang }: Props) {
  const c = COPY[lang][service];
  const items = pickItems(service, lang);
  return (
    <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{c.h}</h2>
      <p className="mt-1 text-sm text-slate-600">{c.sub}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((x) => {
          const target = toTarget(service, lang, x.slug) as any;
          return (
            <Link
              key={x.slug}
              {...target}
              className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-amber-400 hover:shadow-sm"
            >
              <div className="text-2xl">{x.flag}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600 truncate">
                  {lang === "th" ? x.nameTh : x.nameEn}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  {x.badge} · {x.sub}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default HubCountryLinks;
